import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiCheck,
  FiEye, FiCopy, FiAlertTriangle, FiRefreshCw,
  FiChevronDown, FiInfo, FiStar, FiClock,
} from 'react-icons/fi';
import { API_URL, getValidToken } from '../../../utils/authUtils';

/**
 * OutreachTemplateEditor - Manage outreach message templates
 *
 * Features:
 * - List templates by category with usage stats
 * - Create/edit/delete templates
 * - Variable insertion with autocomplete
 * - Live preview with sample or real data
 * - Set default template per category
 *
 * Spec Reference: Phase 10 - Staff UI Polish
 */

const TEMPLATE_CATEGORIES = [
  { value: 'initial', label: 'Initial Outreach' },
  { value: 'follow_up_1', label: 'Follow-up #1' },
  { value: 'follow_up_2', label: 'Follow-up #2' },
  { value: 'final', label: 'Final Attempt' },
  { value: 'board', label: 'Board Pathway' },
  { value: 'hbcu', label: 'HBCU Mentorship' },
  { value: 'peer', label: 'Peer Networking' },
  { value: 'mentorship', label: 'Mentorship' },
  { value: 'custom', label: 'Custom' },
];

const OutreachTemplateEditor = ({ onClose }) => {
  // State
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection state
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');

  // Editor state
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState({
    template_name: '',
    template_category: 'initial',
    subject_template: '',
    body_template: '',
    is_default: false,
  });

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Variable insertion state
  const [showVariableDropdown, setShowVariableDropdown] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  // Saving state
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch templates and variables
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getValidToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [templatesRes, variablesRes] = await Promise.all([
        axios.get(`${API_URL}/api/staff/brokerage/templates`, { headers }),
        axios.get(`${API_URL}/api/staff/brokerage/templates/variables`, { headers }),
      ]);

      setTemplates(templatesRes.data.templates || []);
      setVariables(variablesRes.data.variables || {});

      // Select first template if none selected
      if (templatesRes.data.templates?.length > 0 && !selectedTemplateId) {
        setSelectedTemplateId(templatesRes.data.templates[0].id);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [selectedTemplateId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get selected template
  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  // Filter templates by category
  const filteredTemplates = categoryFilter
    ? templates.filter(t => t.template_category === categoryFilter)
    : templates;

  // Start editing
  const handleEdit = () => {
    if (!selectedTemplate) return;
    setEditedTemplate({
      template_name: selectedTemplate.template_name,
      template_category: selectedTemplate.template_category,
      subject_template: selectedTemplate.subject_template,
      body_template: selectedTemplate.body_template,
      is_default: selectedTemplate.is_default,
    });
    setIsEditing(true);
    setIsCreating(false);
  };

  // Start creating
  const handleCreate = () => {
    setEditedTemplate({
      template_name: '',
      template_category: categoryFilter || 'initial',
      subject_template: '',
      body_template: '',
      is_default: false,
    });
    setIsCreating(true);
    setIsEditing(false);
    setSelectedTemplateId(null);
  };

  // Cancel editing
  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
    setEditedTemplate({
      template_name: '',
      template_category: 'initial',
      subject_template: '',
      body_template: '',
      is_default: false,
    });
  };

  // Save template
  const handleSave = async () => {
    if (!editedTemplate.template_name.trim()) {
      setError('Template name is required');
      return;
    }
    if (!editedTemplate.subject_template.trim()) {
      setError('Subject is required');
      return;
    }
    if (!editedTemplate.body_template.trim()) {
      setError('Body is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = getValidToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      if (isCreating) {
        const response = await axios.post(
          `${API_URL}/api/staff/brokerage/templates`,
          editedTemplate,
          { headers }
        );
        setSelectedTemplateId(response.data.id);
      } else {
        await axios.put(
          `${API_URL}/api/staff/brokerage/templates/${selectedTemplateId}`,
          editedTemplate,
          { headers }
        );
      }

      await fetchData();
      handleCancel();
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const handleDelete = async () => {
    if (!selectedTemplateId) return;

    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const token = getValidToken();
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(
        `${API_URL}/api/staff/brokerage/templates/${selectedTemplateId}`,
        { headers }
      );

      setSelectedTemplateId(null);
      await fetchData();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err.response?.data?.detail || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  // Preview template
  const handlePreview = async () => {
    setPreviewLoading(true);
    setShowPreview(true);

    try {
      const token = getValidToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const templateId = isEditing || isCreating ? null : selectedTemplateId;

      if (templateId) {
        const response = await axios.post(
          `${API_URL}/api/staff/brokerage/templates/${templateId}/preview`,
          { sample_data: true },
          { headers }
        );
        setPreviewData(response.data);
      } else {
        // Preview locally with sample data
        let subject = editedTemplate.subject_template;
        let body = editedTemplate.body_template;

        const sampleValues = {
          target_first_name: 'Jane',
          target_last_name: 'Smith',
          target_company: 'Tech Corp',
          target_title: 'VP of Engineering',
          target_expertise: 'Engineering Leadership',
          industry: 'Technology',
          requester_teaser: 'A mid-career Technology professional seeking mentorship',
          request_type: 'mentorship',
          request_area: 'career transitions',
          consent_link: 'https://app.nacme.org/consent/sample-token',
          staff_name: 'Sample Staff Member',
        };

        Object.entries(sampleValues).forEach(([key, value]) => {
          const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
          subject = subject.replace(regex, value);
          body = body.replace(regex, value);
        });

        setPreviewData({ subject, body });
      }
    } catch (err) {
      console.error('Error previewing template:', err);
      setError(err.response?.data?.detail || 'Failed to preview template');
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Insert variable at cursor position
  const insertVariable = (variableName) => {
    const insertion = `{{${variableName}}}`;
    const field = activeField;

    if (!field) return;

    const ref = field === 'subject' ? subjectRef : bodyRef;
    const textarea = ref.current;

    if (!textarea) {
      // Fallback: append to end
      setEditedTemplate(prev => ({
        ...prev,
        [`${field}_template`]: prev[`${field}_template`] + insertion,
      }));
    } else {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editedTemplate[`${field}_template`];
      const newText = text.substring(0, start) + insertion + text.substring(end);

      setEditedTemplate(prev => ({
        ...prev,
        [`${field}_template`]: newText,
      }));

      // Restore cursor position after insertion
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + insertion.length, start + insertion.length);
      }, 0);
    }

    setShowVariableDropdown(false);
  };

  // Get category label
  const getCategoryLabel = (value) => {
    return TEMPLATE_CATEGORIES.find(c => c.value === value)?.label || value;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading && templates.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8">
          <div className="flex items-center">
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            <span className="ml-3 text-gray-600 dark:text-gray-300">Loading templates...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Outreach Templates
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              New Template
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <FiX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Template List (Left Side) */}
          <div className="w-72 border-r border-gray-200 dark:border-gray-700 flex flex-col">
            {/* Category Filter */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Categories</option>
                {TEMPLATE_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Template List */}
            <div className="flex-1 overflow-y-auto">
              {filteredTemplates.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No templates found
                </div>
              ) : (
                filteredTemplates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => {
                      setSelectedTemplateId(template.id);
                      handleCancel();
                    }}
                    className={`w-full p-3 text-left border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      selectedTemplateId === template.id
                        ? 'bg-cyan-50 dark:bg-cyan-900/20 border-l-4 border-l-cyan-500'
                        : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {template.template_name}
                      </span>
                      {template.is_default && (
                        <FiStar className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {getCategoryLabel(template.template_category)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 mt-1">
                      <span>Used {template.usage_count || 0}x</span>
                      {template.last_used_at && (
                        <>
                          <span>|</span>
                          <span>Last: {formatDate(template.last_used_at)}</span>
                        </>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Editor (Right Side) */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {error && (
              <div className="m-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                <FiAlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            )}

            {(isEditing || isCreating) ? (
              /* Edit/Create Form */
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Template Name & Category */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Template Name
                    </label>
                    <input
                      type="text"
                      value={editedTemplate.template_name}
                      onChange={(e) => setEditedTemplate(prev => ({ ...prev, template_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      placeholder="Enter template name..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category
                    </label>
                    <select
                      value={editedTemplate.template_category}
                      onChange={(e) => setEditedTemplate(prev => ({ ...prev, template_category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      {TEMPLATE_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Set as Default */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isDefault"
                    checked={editedTemplate.is_default}
                    onChange={(e) => setEditedTemplate(prev => ({ ...prev, is_default: e.target.checked }))}
                    className="w-4 h-4 rounded text-cyan-600"
                  />
                  <label htmlFor="isDefault" className="text-sm text-gray-700 dark:text-gray-300">
                    Set as default for this category
                  </label>
                </div>

                {/* Variable Insertion */}
                <div className="relative">
                  <button
                    onClick={() => setShowVariableDropdown(!showVariableDropdown)}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-cyan-600 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-700 rounded-lg hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-colors"
                  >
                    <FiInfo className="w-4 h-4" />
                    Insert Variable
                    <FiChevronDown className={`w-4 h-4 transition-transform ${showVariableDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showVariableDropdown && (
                    <div className="absolute left-0 top-full mt-1 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                      <div className="p-2 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                        Click a variable to insert it. First select a field (Subject or Body).
                      </div>
                      {Object.entries(variables).map(([name, description]) => (
                        <button
                          key={name}
                          onClick={() => insertVariable(name)}
                          disabled={!activeField}
                          className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                          <div className="font-mono text-sm text-cyan-600 dark:text-cyan-400">
                            {`{{${name}}}`}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {description}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Subject Line
                  </label>
                  <input
                    ref={subjectRef}
                    type="text"
                    value={editedTemplate.subject_template}
                    onChange={(e) => setEditedTemplate(prev => ({ ...prev, subject_template: e.target.value }))}
                    onFocus={() => setActiveField('subject')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono text-sm"
                    placeholder="Enter email subject..."
                  />
                </div>

                {/* Body */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Message Body
                  </label>
                  <textarea
                    ref={bodyRef}
                    value={editedTemplate.body_template}
                    onChange={(e) => setEditedTemplate(prev => ({ ...prev, body_template: e.target.value }))}
                    onFocus={() => setActiveField('body')}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono text-sm resize-none"
                    placeholder="Enter message body..."
                  />
                </div>
              </div>
            ) : selectedTemplate ? (
              /* View Template */
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Template Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      {selectedTemplate.template_name}
                      {selectedTemplate.is_default && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full">
                          Default
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {getCategoryLabel(selectedTemplate.template_category)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <FiClock className="w-4 h-4" />
                    <span>Used {selectedTemplate.usage_count || 0} times</span>
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Subject Line
                  </label>
                  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg font-mono text-sm text-gray-900 dark:text-gray-100">
                    {selectedTemplate.subject_template}
                  </div>
                </div>

                {/* Body */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Message Body
                  </label>
                  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg font-mono text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap max-h-96 overflow-y-auto">
                    {selectedTemplate.body_template}
                  </div>
                </div>
              </div>
            ) : (
              /* No Selection */
              <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
                Select a template to view or create a new one
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              {(isEditing || isCreating) ? (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePreview}
                      disabled={previewLoading}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-cyan-600 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-700 rounded-lg hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-colors"
                    >
                      <FiEye className="w-4 h-4" />
                      Preview
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <FiSave className="w-4 h-4" />
                      {saving ? 'Saving...' : (isCreating ? 'Create' : 'Save')}
                    </button>
                  </div>
                </>
              ) : selectedTemplate ? (
                <>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <FiTrash2 className="w-4 h-4" />
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePreview}
                      disabled={previewLoading}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-cyan-600 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-700 rounded-lg hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-colors"
                    >
                      <FiEye className="w-4 h-4" />
                      Preview
                    </button>
                    <button
                      onClick={handleEdit}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors"
                    >
                      <FiEdit2 className="w-4 h-4" />
                      Edit
                    </button>
                  </div>
                </>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Template Preview
                </h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {previewLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : previewData ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Subject
                      </label>
                      <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100">
                        {previewData.subject}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Body
                      </label>
                      <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                        {previewData.body}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    No preview available
                  </div>
                )}
              </div>
              <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutreachTemplateEditor;
